import React, { useState, useCallback, useEffect } from "react";
import {
  Alert, Badge, Button, Card, Col, Container,
  Form, Row, Spinner, Table,
} from "react-bootstrap";
import axios from "../utils/api";
import toast from "react-hot-toast";
import "../styles/InvoiceUpload.css";

const API_URL = process.env.REACT_APP_API_URL_BASE || "http://localhost:6020/";
const DEFAULT_THRESHOLD = 12;

const StatCard = ({ title, value, sub, variant = "primary" }) => (
  <Card className="h-100 text-center shadow-sm">
    <Card.Body>
      <div className={`text-${variant} fw-bold`} style={{ fontSize: "2rem" }}>
        {value ?? "—"}
      </div>
      <div className="fw-semibold">{title}</div>
      {sub && <div className="text-muted small mt-1">{sub}</div>}
    </Card.Body>
  </Card>
);

function InventoryDashboard() {
  const [threshold, setThreshold] = useState(DEFAULT_THRESHOLD);
  const [search, setSearch]       = useState("");
  const [vendor, setVendor]       = useState("");
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState(null);
  const [stats, setStats]         = useState(null);
  const [vendors, setVendors]     = useState([]);
  const [lowStockItems, setLowStockItems] = useState([]);
  const [allItems, setAllItems]           = useState([]);
  const [showAll, setShowAll]             = useState(false);

  const fetchSummary = useCallback(async (thresh = threshold) => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await axios.get(`${API_URL}api/inventory/summary`, {
        params: { threshold: thresh },
      });
      if (!data.success) throw new Error(data.message || "Failed to load");
      setStats(data.stats);
      setVendors(data.vendors || []);
      setLowStockItems(data.lowStockItems);
      setAllItems(data.allItems);
    } catch (err) {
      const msg = err?.response?.data?.message || err.message || "Error";
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }, [threshold]);

  // Load on mount
  useEffect(() => { fetchSummary(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const baseItems = showAll ? allItems : lowStockItems;
  const displayItems = baseItems.filter((i) => {
    const matchesVendor = !vendor || i.vendor === vendor;
    const matchesSearch = !search.trim() ||
      i.upc?.includes(search) ||
      i.description?.toLowerCase().includes(search.toLowerCase());
    return matchesVendor && matchesSearch;
  });

  return (
    <Container className="invoice-upload-container mt-4">
      <div className="d-flex align-items-center justify-content-between mb-4">
        <h2 className="mb-0">Inventory Dashboard</h2>
        <Button variant="outline-primary" size="sm" onClick={() => fetchSummary()} disabled={loading}>
          {loading ? <Spinner animation="border" size="sm" /> : "↻ Refresh"}
        </Button>
      </div>

      {/* ── Slim toolbar ── */}
      <div className="d-flex gap-3 align-items-end mb-4 flex-wrap">
        <div style={{ flex: "1 1 200px" }}>
          <Form.Label className="small fw-semibold mb-1">Vendor</Form.Label>
          <Form.Select value={vendor} onChange={(e) => setVendor(e.target.value)}>
            <option value="">All Vendors</option>
            {vendors.map((v) => (
              <option key={v} value={v}>{v}</option>
            ))}
          </Form.Select>
        </div>
        <div style={{ flex: "1 1 240px" }}>
          <Form.Label className="small fw-semibold mb-1">Search UPC / Description</Form.Label>
          <Form.Control
            type="text"
            placeholder="e.g. 012345678901 or Budweiser"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div style={{ width: 160 }}>
          <Form.Label className="small fw-semibold mb-1">
            Low Stock Threshold <span className="text-muted">(bottles)</span>
          </Form.Label>
          <Form.Control
            type="number"
            min={1}
            value={threshold}
            onChange={(e) => {
              const val = Number(e.target.value) || DEFAULT_THRESHOLD;
              setThreshold(val);
              fetchSummary(val);
            }}
          />
        </div>
      </div>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* ── Stat Cards ── */}
      {stats && (
        <Row className="g-3 mb-4">
          <Col xs={6} md={2}>
            <StatCard title="Total SKUs" value={stats.totalSkus.toLocaleString()} variant="primary" />
          </Col>
          <Col xs={6} md={2}>
            <StatCard
              title="Units Received"
              value={stats.totalReceived.toLocaleString()}
              variant="secondary"
            />
          </Col>
          <Col xs={6} md={2}>
            <StatCard
              title="Units Sold"
              value={stats.totalSold.toLocaleString()}
              variant="info"
            />
          </Col>
          <Col xs={6} md={2}>
            <StatCard
              title="Remaining"
              value={stats.totalRemaining.toLocaleString()}
              variant="success"
            />
          </Col>
          <Col xs={6} md={2}>
            <StatCard
              title="Low Stock"
              value={stats.lowStockCount.toLocaleString()}
              sub={`≤ ${stats.lowStockThreshold} units`}
              variant="danger"
            />
          </Col>
          <Col xs={6} md={2}>
            <StatCard
              title="Revenue"
              value={`$${stats.totalRevenue.toLocaleString()}`}
              variant="warning"
            />
          </Col>
        </Row>
      )}

      {/* ── Items Table ── */}
      {stats && (
        <div className="table-section card p-4">
          <div className="d-flex align-items-center justify-content-between mb-3">
            <h5 className="mb-0">
              {showAll ? "All Items" : "Low Stock Items"}{" "}
              <Badge bg={showAll ? "secondary" : "danger"}>
                {displayItems.length.toLocaleString()}
              </Badge>
            </h5>
            <Button
              variant={showAll ? "outline-danger" : "outline-secondary"}
              size="sm"
              onClick={() => setShowAll((v) => !v)}
            >
              {showAll ? `Show Low Stock Only (${lowStockItems.length})` : "Show All Items"}
            </Button>
          </div>

          {displayItems.length === 0 ? (
            <Alert variant="success" className="mb-0">
              {showAll ? "No items found." : "No low-stock items — looking good! 🎉"}
            </Alert>
          ) : (
            <div className="table-responsive sticky-table sticky-table--inner">
              <Table striped bordered hover size="sm">
                <thead className="table-light">
                  <tr>
                    <th>#</th>
                    <th>Vendor</th>
                    <th>UPC</th>
                    <th>Description</th>
                    <th className="text-end">Cases In</th>
                    <th className="text-end">Bottles In<br/><span className="text-muted fw-normal" style={{fontSize:"0.75rem"}}>cases × ppc</span></th>
                    <th className="text-end">Bottles Sold</th>
                    <th className="text-end">Remaining<br/><span className="text-muted fw-normal" style={{fontSize:"0.75rem"}}>(bottles)</span></th>
                    <th className="text-end">Cases Left</th>
                    <th className="text-end">Revenue</th>
                    <th>Last Sold</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {displayItems.map((item, idx) => (
                    <tr key={item.upc} className={item.lowStock ? "table-danger" : ""}>
                      <td>{idx + 1}</td>
                      <td className="text-nowrap">{item.vendor || <span className="text-muted">—</span>}</td>
                      <td className="text-nowrap">{item.upc}</td>
                      <td>{item.description || <span className="text-muted">—</span>}</td>
                      <td className="text-end">{item.casesReceived.toLocaleString()}</td>
                      <td className="text-end">{item.unitsReceived.toLocaleString()}</td>
                      <td className="text-end">{item.unitsSold.toLocaleString()}</td>
                      <td className="text-end fw-semibold">{item.remaining.toLocaleString()}</td>
                      <td className="text-end">{item.casesRemaining}</td>
                      <td className="text-end">
                        {item.revenue > 0 ? `$${item.revenue.toLocaleString()}` : <span className="text-muted">—</span>}
                      </td>
                      <td className="text-nowrap">{item.lastSold || <span className="text-muted">—</span>}</td>
                      <td>
                        {item.remaining <= 0 ? (
                          <Badge bg="dark">Out of Stock</Badge>
                        ) : item.lowStock ? (
                          <Badge bg="danger">Low Stock</Badge>
                        ) : (
                          <Badge bg="success">OK</Badge>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </div>
      )}
    </Container>
  );
}

export default InventoryDashboard;
